// src/pages/backups/RouterBackupsPage.tsx

import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Database,
  Download,
  FileArchive,
  HardDrive,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Server,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface BackupFile {
  name: string;
  type: string;
  size: string;
  creation_time: string | null;
}

type RouterId = string | number;

interface RouterBackupRow {
  router_id: RouterId;
  router_name: string;
  router_ip: string;
  files: BackupFile[];
  error?: string;
}

type ConfirmMode = "create" | "restore" | "delete";

interface ConfirmState {
  mode: ConfirmMode;
  routerId?: RouterId;
  routerName?: string;
  filename?: string;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getFileTone = (file: BackupFile) => {
  const name = `${file.name} ${file.type}`.toLowerCase();

  if (name.includes(".backup") || name.includes("backup")) {
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  }

  if (name.includes(".rsc") || name.includes("script")) {
    return "border-sky-200 bg-sky-100 text-sky-800";
  }

  if (name.includes("directory") || name.includes("disk")) {
    return "border-amber-200 bg-amber-100 text-amber-800";
  }

  return "border-slate-300 bg-slate-200 text-slate-600";
};

const isBackupFile = (file: BackupFile) => {
  const value = `${file.name} ${file.type}`.toLowerCase();
  return value.includes(".backup") || value.includes("backup");
};

const LoadingState: React.FC<{ label: string }> = ({ label }) => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            {label}
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while router backup information is being prepared.
          </p>

          <div className="relative mt-6 flex justify-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-teal-500 delay-150" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-500 delay-300" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal: React.FC<{
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  variant: "default" | "danger";
  loading: boolean;
  routers: RouterBackupRow[];
  confirmState: ConfirmState | null;
  createRouterId: RouterId | "";
  onCreateRouterChange: (id: RouterId | "") => void;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({
  open,
  title,
  description,
  confirmLabel,
  variant,
  loading,
  routers,
  confirmState,
  createRouterId,
  onCreateRouterChange,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  const danger = variant === "danger";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
                danger
                  ? "bg-rose-100 text-rose-700"
                  : "bg-emerald-100 text-emerald-700"
              }`}
            >
              {danger ? (
                <ShieldAlert className="h-5 w-5" />
              ) : (
                <CheckCircle2 className="h-5 w-5" />
              )}
            </div>

            <div>
              <p className="text-[15px] font-black text-slate-900">{title}</p>
              <p className="mt-1 text-[12.5px] font-medium leading-relaxed text-slate-500">
                {description}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {confirmState?.mode === "create" && (
          <div className="border-b border-slate-200 px-5 py-4">
            <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
              Select router
            </label>

            <select
              value={String(createRouterId)}
              onChange={(e) => onCreateRouterChange(e.target.value)}
              disabled={loading}
              className={`${inputCls} cursor-pointer`}
            >
              <option value="">Choose router...</option>
              {routers.map((router) => (
                <option
                  key={String(router.router_id)}
                  value={String(router.router_id)}
                >
                  {router.router_name}
                </option>
              ))}
            </select>

            <p className="mt-2 text-[12px] font-medium text-slate-500">
              The backup will be created on the selected MikroTik router.
            </p>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-100/90 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-700 hover:bg-slate-100`}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={
              loading ||
              (confirmState?.mode === "create" && !String(createRouterId))
            }
            className={`${btnBase} h-11 px-5 text-white ${
              danger
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Processing…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const RouterBackupsPage: React.FC = () => {
  const { showToast } = useToast();

  const [rows, setRows] = useState<RouterBackupRow[]>([]);
  const [selectedRouterId, setSelectedRouterId] = useState<RouterId | "">("");

  const [loadingList, setLoadingList] = useState(false);
  const [reloadingRouter, setReloadingRouter] = useState(false);
  const [fileSearch, setFileSearch] = useState("");

  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [createRouterId, setCreateRouterId] = useState<RouterId | "">("");

  const selectedRouter = useMemo(
    () =>
      rows.find((row) => String(row.router_id) === String(selectedRouterId)) ||
      null,
    [rows, selectedRouterId],
  );

  const totalFiles = useMemo(
    () => rows.reduce((sum, row) => sum + (row.files?.length || 0), 0),
    [rows],
  );

  const totalBackups = useMemo(
    () =>
      rows.reduce(
        (sum, row) => sum + (row.files || []).filter(isBackupFile).length,
        0,
      ),
    [rows],
  );

  const failedRouters = useMemo(
    () => rows.filter((row) => Boolean(row.error)),
    [rows],
  );

  const filteredFiles = useMemo(() => {
    const files = selectedRouter?.files || [];
    const q = fileSearch.trim().toLowerCase();

    if (!q) return files;

    return files.filter((file) =>
      [file.name, file.type, file.size, file.creation_time || ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [selectedRouter, fileSearch]);

  const newestBackup = useMemo(() => {
    const files = selectedRouter?.files || [];

    return [...files]
      .filter((file) => file.creation_time && isBackupFile(file))
      .sort(
        (a, b) =>
          new Date(b.creation_time || "").getTime() -
          new Date(a.creation_time || "").getTime(),
      )[0];
  }, [selectedRouter]);

  const loadAllRouters = async () => {
    setLoadingList(true);
    setError(null);

    try {
      const data = await fetchApp<RouterBackupRow[]>(
        "/mikrotik/backup/client/all-files",
      );

      const nextRows = Array.isArray(data) ? data : [];

      setRows(nextRows);

      setSelectedRouterId((current) => {
        if (
          current &&
          nextRows.some((row) => String(row.router_id) === String(current))
        ) {
          return current;
        }

        return nextRows[0]?.router_id ?? "";
      });

      setCreateRouterId((current) => {
        if (
          current &&
          nextRows.some((row) => String(row.router_id) === String(current))
        ) {
          return current;
        }

        return nextRows[0]?.router_id ?? "";
      });
    } catch (err: any) {
      console.error(err);
      const msg = err?.message || "Failed to load router backups";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoadingList(false);
    }
  };

  const reloadRouterById = async (routerId: RouterId) => {
    const files = await fetchApp<BackupFile[]>(
      `/mikrotik/backup/${routerId}/files`,
    );

    setRows((prev) =>
      prev.map((row) =>
        String(row.router_id) === String(routerId)
          ? {
              ...row,
              files: Array.isArray(files) ? files : [],
              error: undefined,
            }
          : row,
      ),
    );
  };

  const reloadSelectedRouter = async () => {
    if (!selectedRouter) return;

    setReloadingRouter(true);
    setError(null);

    try {
      await reloadRouterById(selectedRouter.router_id);
    } catch (err: any) {
      console.error(err);
      const msg = err?.message || "Failed to refresh backup list";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setReloadingRouter(false);
    }
  };

  const createBackup = async (routerId: RouterId) => {
    setConfirmLoading(true);

    try {
      const res = await fetchApp<any>(`/mikrotik/backup/${routerId}/create`, {
        method: "POST",
      });

      showToast(res?.message || "Backup created successfully", "success");

      await reloadRouterById(routerId);

      setSelectedRouterId(routerId);
    } catch (err: any) {
      console.error(err);
      const msg = err?.message || "Failed to create backup";
      showToast(msg, "error");
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmState(null);
    }
  };

  const restoreBackup = async (routerId: RouterId, filename: string) => {
    setConfirmLoading(true);

    try {
      const res = await fetchApp<any>(
        `/mikrotik/backup/${routerId}/restore/${encodeURIComponent(filename)}`,
        { method: "POST" },
      );

      showToast(
        res?.message || "Backup restore command sent successfully",
        "success",
      );
    } catch (err: any) {
      console.error(err);
      const msg = err?.message || "Failed to restore backup";
      showToast(msg, "error");
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmState(null);
    }
  };

  const deleteBackup = async (routerId: RouterId, filename: string) => {
    setConfirmLoading(true);

    try {
      const res = await fetchApp<any>(
        `/mikrotik/backup/${routerId}/files/${encodeURIComponent(filename)}`,
        { method: "DELETE" },
      );

      showToast(res?.message || "Backup deleted successfully", "success");
      await reloadRouterById(routerId);
    } catch (err: any) {
      console.error(err);
      const msg = err?.message || "Failed to delete backup";
      showToast(msg, "error");
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmState(null);
    }
  };

  const downloadBackup = async (file: BackupFile) => {
    if (!selectedRouter) return;

    try {
      const res = await fetchApp<any>(
        `/mikrotik/backup/${selectedRouter.router_id}/download/${encodeURIComponent(
          file.name,
        )}`,
      );

      if (res?.download_url) {
        window.open(res.download_url, "_blank", "noopener,noreferrer");
        return;
      }

      showToast("Download request sent successfully", "success");
    } catch (err: any) {
      console.error(err);
      showToast(err?.message || "Failed to download backup", "error");
    }
  };

  const openConfirmCreate = () => {
    setCreateRouterId(selectedRouter?.router_id ?? rows[0]?.router_id ?? "");
    setConfirmState({
      mode: "create",
    });
    setConfirmOpen(true);
  };

  const openConfirmRestore = (file: BackupFile) => {
    if (!selectedRouter) return;

    setConfirmState({
      mode: "restore",
      routerId: selectedRouter.router_id,
      routerName: selectedRouter.router_name,
      filename: file.name,
    });
    setConfirmOpen(true);
  };

  const openConfirmDelete = (file: BackupFile) => {
    if (!selectedRouter) return;

    setConfirmState({
      mode: "delete",
      routerId: selectedRouter.router_id,
      routerName: selectedRouter.router_name,
      filename: file.name,
    });
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!confirmState) return;

    if (confirmState.mode === "create") {
      if (!createRouterId) {
        showToast("Please select a router first", "error");
        return;
      }

      createBackup(createRouterId);
      return;
    }

    if (
      confirmState.mode === "restore" &&
      confirmState.routerId &&
      confirmState.filename
    ) {
      restoreBackup(confirmState.routerId, confirmState.filename);
      return;
    }

    if (
      confirmState.mode === "delete" &&
      confirmState.routerId &&
      confirmState.filename
    ) {
      deleteBackup(confirmState.routerId, confirmState.filename);
    }
  };

  const closeConfirm = () => {
    if (confirmLoading) return;

    setConfirmOpen(false);
    setConfirmState(null);
  };

  const confirmTitle = useMemo(() => {
    if (!confirmState) return "";

    if (confirmState.mode === "create") return "Create router backup?";
    if (confirmState.mode === "restore")
      return "Restore configuration from backup?";
    if (confirmState.mode === "delete") return "Delete backup file?";

    return "";
  }, [confirmState]);

  const confirmDescription = useMemo(() => {
    if (!confirmState) return "";

    if (confirmState.mode === "create") {
      return "Choose the router you want to back up. A new configuration backup will be created on that MikroTik router.";
    }

    const routerName =
      confirmState.routerName || `Router ${confirmState.routerId}`;

    if (confirmState.mode === "restore") {
      return `The backup “${confirmState.filename}” will be restored on ${routerName}. This may overwrite the current configuration and may reboot the router.`;
    }

    if (confirmState.mode === "delete") {
      return `The backup file “${confirmState.filename}” will be permanently removed from ${routerName}. This cannot be undone.`;
    }

    return "";
  }, [confirmState]);

  const confirmLabel = useMemo(() => {
    if (!confirmState) return "Confirm";

    if (confirmState.mode === "create") return "Create backup";
    if (confirmState.mode === "restore") return "Restore backup";
    if (confirmState.mode === "delete") return "Delete backup";

    return "Confirm";
  }, [confirmState]);

  const confirmVariant: "danger" | "default" =
    confirmState?.mode === "delete" || confirmState?.mode === "restore"
      ? "danger"
      : "default";

  useEffect(() => {
    loadAllRouters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingList && rows.length === 0) {
    return <LoadingState label="Loading router backups…" />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  MikroTik Backup Management
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Router Backups
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  View, create, restore, download, and delete MikroTik
                  configuration backups for your routers.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={loadAllRouters}
                  disabled={loadingList || reloadingRouter || confirmLoading}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {loadingList ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh All
                </button>

                <button
                  type="button"
                  onClick={openConfirmCreate}
                  disabled={rows.length === 0 || confirmLoading}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  Create Backup
                </button>
              </div>
            </div>
          </div>
        </div>

        {(loadingList || reloadingRouter) && rows.length > 0 && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            {reloadingRouter
              ? "Refreshing selected router backup files…"
              : "Refreshing router backups…"}
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {failedRouters.length > 0 && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-800 shadow-sm shadow-amber-200/40 sm:px-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <ShieldAlert className="h-5 w-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[13.5px] font-black">
                  Some routers returned errors
                </p>

                <div className="mt-2 space-y-1">
                  {failedRouters.slice(0, 4).map((router) => (
                    <p
                      key={String(router.router_id)}
                      className="text-[12px] font-medium text-amber-700"
                    >
                      <span className="font-black">{router.router_name}</span>:{" "}
                      {router.error || "Failed to load backup files"}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Routers"
            value={rows.length}
            sub="Routers with backup access"
            icon={<Server className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Backup Files"
            value={totalBackups}
            sub="Real backup files only"
            icon={<Database className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Selected Router"
            value={selectedRouter?.files?.length || 0}
            sub={selectedRouter?.router_name || "No router selected"}
            icon={<HardDrive className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="All Files"
            value={totalFiles}
            sub="All router file entries"
            icon={<FileArchive className="h-5 w-5" />}
            tone="amber"
          />
        </div>

        {/* Main Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <FileArchive className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Backup Files
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  {selectedRouter
                    ? `${filteredFiles.length} of ${
                        selectedRouter.files?.length || 0
                      } files on ${selectedRouter.router_name}`
                    : "Select a router to view backup files"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:flex xl:items-center">
              <div className="relative w-full xl:w-80">
                <Server className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={String(selectedRouterId)}
                  onChange={(e) => {
                    setSelectedRouterId(e.target.value);
                    setFileSearch("");
                  }}
                  className={`${inputCls} cursor-pointer pl-10`}
                >
                  <option value="">Select router...</option>

                  {rows.map((router) => (
                    <option
                      key={String(router.router_id)}
                      value={String(router.router_id)}
                    >
                      {router.router_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative w-full xl:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={fileSearch}
                  onChange={(e) => setFileSearch(e.target.value)}
                  placeholder="Search backup file..."
                  className={`${inputCls} pl-10`}
                />

                {fileSearch && (
                  <button
                    type="button"
                    onClick={() => setFileSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={reloadSelectedRouter}
                disabled={!selectedRouter || reloadingRouter}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {reloadingRouter ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </button>
            </div>
          </div>

          {selectedRouter && (
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-[13px] font-black text-slate-800">
                    {selectedRouter.router_name}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                    <HardDrive className="h-3 w-3" />
                    {selectedRouter.files?.length || 0} files
                  </span>

                  <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                    <Clock className="h-3 w-3" />
                    Latest backup:{" "}
                    {newestBackup
                      ? formatDateTime(newestBackup.creation_time)
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">File</th>
                  <th className="px-5 py-3.5 font-black">Type</th>
                  <th className="px-5 py-3.5 font-black">Size</th>
                  <th className="px-5 py-3.5 font-black">Created</th>
                  <th className="px-5 py-3.5 text-right font-black">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {!selectedRouter ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Server className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        Select a router
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        Use the router dropdown above to view backup files.
                      </p>
                    </td>
                  </tr>
                ) : reloadingRouter ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center">
                      <div className="flex items-center justify-center gap-2 text-[13px] font-bold text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                        Loading backup files…
                      </div>
                    </td>
                  </tr>
                ) : filteredFiles.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <FileArchive className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No backup files found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        Create a new backup or clear your search filter.
                      </p>

                      {fileSearch && (
                        <button
                          type="button"
                          onClick={() => setFileSearch("")}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear search
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredFiles.map((file) => (
                    <tr
                      key={file.name}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                            <FileArchive className="h-4 w-4" />
                          </div>

                          <div className="min-w-0">
                            <p className="truncate font-mono text-[12.5px] font-black text-slate-900">
                              {file.name}
                            </p>

                            <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                              Router: {selectedRouter.router_name}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-black ${getFileTone(
                            file,
                          )}`}
                        >
                          {file.type || "file"}
                        </span>
                      </td>

                      <td className="px-5 py-4 font-mono text-[12px] font-black text-slate-700">
                        {file.size && file.size !== "None" ? file.size : "—"}
                      </td>

                      <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                        {formatDateTime(file.creation_time)}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => downloadBackup(file)}
                            className={`${btnBase} h-9 border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                          >
                            <Download className="h-3.5 w-3.5" />
                            Download
                          </button>

                          <button
                            type="button"
                            onClick={() => openConfirmRestore(file)}
                            className={`${btnBase} h-9 border border-amber-200 bg-amber-100 px-3 text-amber-800 hover:bg-amber-200`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Restore
                          </button>

                          <button
                            type="button"
                            onClick={() => openConfirmDelete(file)}
                            className={`${btnBase} h-9 border border-rose-200 bg-rose-100 px-3 text-rose-700 hover:bg-rose-200`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {!selectedRouter ? (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Server className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  Select a router
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Use the router dropdown above to view backup files.
                </p>
              </div>
            ) : reloadingRouter ? (
              <div className="px-4 py-12 text-center">
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-emerald-500" />
                <p className="mt-2 text-[13px] font-bold text-slate-500">
                  Loading backup files…
                </p>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <FileArchive className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No backup files found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Create a backup or clear your search filter.
                </p>
              </div>
            ) : (
              filteredFiles.map((file) => (
                <div key={file.name} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-[13px] font-black text-slate-900">
                        {file.name}
                      </p>

                      <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                        {formatDateTime(file.creation_time)}
                      </p>
                    </div>

                    <span
                      className={`inline-flex shrink-0 rounded-full border px-2.5 py-1 text-[10.5px] font-black ${getFileTone(
                        file,
                      )}`}
                    >
                      {file.type || "file"}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Size
                      </p>

                      <p className="mt-1 font-mono text-[12.5px] font-black text-slate-900">
                        {file.size && file.size !== "None" ? file.size : "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                        Router
                      </p>

                      <p className="mt-1 truncate text-[12.5px] font-black text-slate-900">
                        {selectedRouter.router_name}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <button
                      type="button"
                      onClick={() => downloadBackup(file)}
                      className={`${btnBase} h-10 border border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </button>

                    <button
                      type="button"
                      onClick={() => openConfirmRestore(file)}
                      className={`${btnBase} h-10 border border-amber-200 bg-amber-100 text-amber-800 hover:bg-amber-200`}
                    >
                      <RotateCcw className="h-4 w-4" />
                      Restore
                    </button>

                    <button
                      type="button"
                      onClick={() => openConfirmDelete(file)}
                      className={`${btnBase} h-10 border border-rose-200 bg-rose-100 text-rose-700 hover:bg-rose-200`}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredFiles.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {selectedRouter?.files?.length || 0}
              </span>{" "}
              files
            </p>

            {fileSearch && (
              <button
                type="button"
                onClick={() => setFileSearch("")}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear search
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirmLabel}
        variant={confirmVariant}
        loading={confirmLoading}
        routers={rows}
        confirmState={confirmState}
        createRouterId={createRouterId}
        onCreateRouterChange={setCreateRouterId}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default RouterBackupsPage;
